import { Component, OnDestroy, OnInit } from '@angular/core';

import { RealtimeService } from '../../../services/realtime.service';
import { Viewer } from './models/viewer.model';
import { ViewSession } from './models/view-session.model';

import { DeviceInfo } from './models/device-info.model';

import { Subscription, interval } from 'rxjs';

export class RealtimeMediaclip {
	id = '';
	title = '';
	count = 0;
	style = '';
	url = '';

	constructor(id?: string, title?: string, url?: string) {
		this.id = id;
		this.title = title;
		this.url = url;
	}
}

export class RealtimeDevice {
	title = '';
	count = 0;
	style = '';
	deviceInfo: DeviceInfo = null;

	constructor(title?: string, deviceInfo?: DeviceInfo) {
		this.title = title;
		this.deviceInfo = deviceInfo;
	}
}

export class RealtimeItem {
	title = '';
	count = 0;
	style = '';
	items: RealtimeMediaclip[];
	url = '';

	constructor(title?: string, url?: string) {
		this.title = title;
		this.url = url;
	}
}

enum Types {
	toplist = 1,
	referrers = 2,
	domains = 3,
	devices = 4
}

@Component({
	selector: 'realtime-audience-toplist',
	templateUrl: './realtime-audience-toplist.component.html',
	styleUrls: ['./realtime-audience-toplist.component.scss']
})

export class RealtimeAudienceToplistComponent implements OnInit, OnDestroy {
	devicesOutput: RealtimeDevice[];
	referrersOutput: RealtimeItem[];
	toplistOutput: RealtimeMediaclip[];
	updateInterval: number;

	private toplist: RealtimeMediaclip[];
	private intervalSubscription: Subscription;
	private domainShown: string;

	private domains: RealtimeItem[];

	private devices: RealtimeDevice[];
	private unknownDevice: DeviceInfo;

	constructor(private realtimeService: RealtimeService) {
		this.toplist = new Array<RealtimeMediaclip>();
		this.toplistOutput = new Array<RealtimeMediaclip>();
		this.referrersOutput = new Array<RealtimeItem>();
		this.devices = new Array<RealtimeDevice>();
		this.devicesOutput = new Array<RealtimeDevice>();
		this.domains = new Array<RealtimeItem>();
		this.domainShown = null;
		this.updateInterval = 3;
		this.unknownDevice = {
			browserName_str: '',
			browserVersion_str: '',
			deviceType_str: '',
			osName_str: 'Unknown',
			osVersion_str: ''
		};
	}

	createGradient(percentage: number): string {
		return 'linear-gradient(to right, #7ab1df 0%,#7ab1df ' + percentage
			+ '%, rgba(141,171,196,0.25) ' + percentage + '%, rgba(141,171,196,0.25) 100%)';
	}

	ngOnInit() {
		if (!this.intervalSubscription) {
			this.intervalSubscription = interval(1000).subscribe((iteration) => {
				if (iteration % this.updateInterval === 0) {
					this.updateLists();
				}
			});
		}
	}

	ngOnDestroy() {
		if (this.intervalSubscription) {
			this.intervalSubscription.unsubscribe();
		}
	}

	updateLists() {
		const found = this.realtimeService.viewers.filter((m: Viewer) => m.activeSession && m.activeSession.currentMediaclip);

		this.toplist = new Array<RealtimeMediaclip>();
		this.devices = new Array<RealtimeDevice>();
		this.domains = new Array<RealtimeItem>();

		if (found.length > 0) {
			for (let i = 0; i < found.length; i++) {
				const viewer: Viewer = found[i];
				const activeSession: ViewSession = viewer.activeSession;
				const mediaclip = activeSession.currentMediaclip;

				if (mediaclip && mediaclip.id) {
					this.updateCount(Types.toplist, new RealtimeMediaclip(mediaclip.id, mediaclip.title), true);
				}
				let domainIndex = -1;
				if (activeSession.domain) {
					const domain = activeSession.domain.replace('www.', '');
					domainIndex = this.updateCount(Types.domains, new RealtimeItem(domain), true);
				}
				if (activeSession.referrer) {
					let referrer = activeSession.referrer.replace(/https?:\/\/(?:www.)?/g, '');
					if (referrer === '..' && !referrer.match(/\/$/)) {
						referrer += '/';
					}
					const domain = referrer.replace(/\/.*/g, '');

					if (domainIndex === -1) {
						if (domain.length > 0) {
							domainIndex = this.updateCount(Types.domains, new RealtimeItem(domain), true);
						}
					}
					referrer = referrer.replace(domain, '..');
					if (domainIndex > -1) {
						this.updateCount(Types.referrers, new RealtimeMediaclip(null, referrer, activeSession.referrer), true, domainIndex);
					}
				}
				let deviceInfo: DeviceInfo = this.unknownDevice;
				if (viewer.deviceInfo && viewer.deviceInfo.osName_str) {
					deviceInfo = viewer.deviceInfo;
				}
				this.updateCount(Types.devices, new RealtimeDevice(deviceInfo.osName_str, deviceInfo as DeviceInfo), true);
			}
		}
		this.sortAndUpdateList(Types.toplist);

		this.sortAndUpdateList(Types.domains);

		this.sortAndUpdateList(Types.devices);
	}


	updateCount(type: Types, currentItem: RealtimeMediaclip | RealtimeItem | RealtimeDevice, add: boolean = true, index: number = 0) {
		if (currentItem) {
			let list: any[];
			if (type === Types.referrers) {
				if (!this.domains || this.domains.length === 0) {
					return;
				}
				if (!this.domains[index].items) {
					this.domains[index].items = new Array<RealtimeMediaclip>();
				}
				list = this.domains[index].items;
			} else if (type === Types.domains) {
				list = this.domains;
			} else if (type === Types.toplist) {
				list = this.toplist;
			} else {
				list = this.devices;
			}
			let key: string;
			let found;
			if (currentItem instanceof RealtimeMediaclip && type === Types.toplist) {
				key = currentItem.id;
				found = list.filter((m) => m.id === key);
			} else {
				key = currentItem.title;
				found = list.filter((m) => m.title === key);
			}

			let item: RealtimeMediaclip | RealtimeItem | RealtimeDevice;
			if (found.length === 0) {
				item = currentItem;
				list.push(item);
			} else {
				item = found[0];
				if (!item.title || item.title.length === 0) {
					item.title = currentItem.title;
				}
			}
			if (add) {
				item.count++;
			} else {
				item.count--;
			}
			const location = list.indexOf(item);
			if (item.count <= 0) {
				if (location > -1) {
					list.splice(location, 1);
				}
			}
			return location;
		}
		return -1;
	}

	totalCount(list: (RealtimeMediaclip | RealtimeItem | RealtimeDevice)[]): number {
		let total = 0;
		const length = list.length;
		if (length > 0) {
			for (let i = 0; i < length; i++) {
				total += list[i].count;
			}
		}
		return total;
	}

	sortAndUpdateList(type: Types, max: number = 10, offset: number = 0) {
		switch (type) {
			case Types.toplist:
				this.toplist.sort((a, b) => b.count - a.count);
				const tempList = this.toplist.slice(offset, max);

				this.updateListItems(tempList, this.toplistOutput);
				break;
			case Types.devices:
				this.devices.sort((a, b) => b.count - a.count);

				this.updateListItems(this.devices, this.devicesOutput);
				break;
			case Types.domains:
				this.domains.sort((a, b) => b.count - a.count);
				if (this.domains.length > 20) {
					this.domains = this.domains.slice(offset, 20);
				}
				this.updateListItems(this.domains, this.referrersOutput, 5);
				break;
		}
	}

	updateListItems(
		list: (RealtimeMediaclip | RealtimeItem | RealtimeDevice)[], outputList: (RealtimeMediaclip | RealtimeItem | RealtimeDevice)[], max: number = null
	) {
		const totalCount = this.totalCount(list);

		const length = list.length;
		for (let i = 0; i < length; i++) {
			if (i < outputList.length) {
				if (list[i].title !== outputList[i].title || list[i].count !== outputList[i].count) {
					list[i].style = this.getItemStyle(list[i].count, totalCount);
					outputList[i] = list[i];
				}

			} else {
				list[i].style = this.getItemStyle(list[i].count, totalCount);
				outputList[i] = list[i];
			}
			if (list[i] instanceof RealtimeItem) {
				const outputItem: RealtimeItem = outputList[i] as RealtimeItem;
				if (list[i].title === this.domainShown) {
					const item: RealtimeItem = list[i] as RealtimeItem;
					if (item.items.length > 0) {
						let items = item.items;
						items.sort((a, b) => b.count - a.count);
						if (max) {
							items = items.slice(0, max);
						}
						this.updateListItems(items, outputItem.items);
					}
				} else {
					outputItem.items = new Array<RealtimeMediaclip>();
				}
			}
		}
		while (length < outputList.length) {
			outputList.pop();
		}
	}

	getItemStyle(itemCount: number, totalCount: number) {
		return this.createGradient(itemCount / totalCount * 100);
	}

	openSubitems(event: MouseEvent, title: string) {
		if (title !== this.domainShown) {
			this.domainShown = title;
			this.updateLists();
		} else {
			this.domainShown = null;
		}
		this.sortAndUpdateList(Types.domains);
	}
}
